import { NgModule, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import {AppRoutingModule, goToSafePlaceLogin} from './app-routing.module';
import { AppComponent } from './app.component';
import { SharedModule } from '../../../aop-eop-core/src/app/shared/shared.module';
import { ConsoleComponent } from './console/console.component';
import {MatSidenav, MatSidenavContainer, MatSidenavModule} from '@angular/material/sidenav';
import { AshgridxLogoComponent } from './ashgridx-logo/ashgridx-logo.component';
import { AuditModule } from './audit/audit.module';
import { NgxEchartsModule } from 'ngx-echarts';

// Import the echarts core module, which provides the necessary interfaces for using echarts.
import * as echarts from 'echarts/core';

// Import bar charts, all suffixed with Chart
import { BarChart, PieChart, LineChart, } from 'echarts/charts';

// Import the tooltip, title, rectangular coordinate system, dataset and transform components
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  LegendComponent,
  MarkLineComponent,
  MarkPointComponent,
} from 'echarts/components';

// Features like Universal Transition and Label Layout
import { LabelLayout, UniversalTransition } from 'echarts/features';

// Import the Canvas renderer
// Note that including the CanvasRenderer or SVGRenderer is a required step
import { CanvasRenderer } from 'echarts/renderers';

// Import the theme
import 'echarts/theme/macarons.js';
import { AccountSettingsComponent } from './account-settings/account-settings.component';
import { TeamMembersComponent } from './team-members/team-members.component';
import { AddTeamMembersComponent } from './add-team-members/add-team-members.component';
import { SwitchWorkspaceComponent } from './switch-workspace/switch-workspace.component';
import { BillingComponent } from './billing/billing.component';
import { BillingActivityComponent } from './billing-activity/billing-activity.component';
import { BillingMonthlyCostComponent } from './billing-monthly-cost/billing-monthly-cost.component';
import { BillingPaymentMethodComponent } from './billing-payment-method/billing-payment-method.component';
import { AddPaymentMethodComponent } from './add-payment-method/add-payment-method.component';
import { CreateNewSiteComponent } from './create-new-site/create-new-site.component';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn, HttpStatusCode,
  provideHttpClient,
  withFetch, withInterceptors
} from '@angular/common/http';
import {SESSION_TOKEN_KEY} from "../../../aop-eop-core/src/app/constants/local-storage.constant";
import { catchError, throwError, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { SiteComponent } from './site/site.component';
import {IWorkspace} from "./interfaces/workspace.interface";
import { ViewSiteComponent } from './view-site/view-site.component';
import {GoogleMap, MapInfoWindow, MapMarker} from "@angular/google-maps";
import {
  MatExpansionModule,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import {AshGridXModule} from "./ashgridx/ash-grid-x.module";
import {MonacoEditorModule} from "ngx-monaco-editor-v2";
import {ConsoleWrapperComponent} from "./console-wrapper/console-wrapper.component";
import {
  progressBarInterceptor
} from "../../../aop-eop-core/src/app/interceptors/progress-bar-request-interceptor.interceptor";
import {SurechargeModule} from "./surecharge/surecharge.module";
import { CompanyDetailsComponent } from './company-details/company-details.component';
import { ProfileDetailsComponent } from './profile-details/profile-details.component';
import { ManagePrivilegeComponent } from './manage-privilege/manage-privilege.component';
import { AddPrivilegeComponent } from './add-privilege/add-privilege.component';
import { PrivilegeComponent } from './privilege/privilege.component';
import { NoWorkspaceComponent } from './no-workspace/no-workspace.component';
import { EditUserProfileComponent } from './edit-user-profile/edit-user-profile.component';
import {AngularFireModule} from "@angular/fire/compat";
import {AngularFireAuthModule} from "@angular/fire/compat/auth";
import {getStorage, provideStorage } from '@angular/fire/storage';
import {AngularFireStorageModule} from "@angular/fire/compat/storage";
import { NoPrivilegeComponent } from './no-privilege/no-privilege.component';

// Register the required components
echarts.use([
  BarChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  LabelLayout,
  UniversalTransition,
  CanvasRenderer,
  PieChart,
  LineChart,
  LegendComponent,
  MarkLineComponent,
  MarkPointComponent,
]);

export const authTokenInterceptor: HttpInterceptorFn = (request, next) => {
  const isProduction = environment.production;
  const workspace = localStorage.getItem('selectedWorkspace');

  const url = request.url;
  const isExempted = url.includes('/workspace/mine') || url.includes('/user/mine');

  let headers = isExempted ?
    request.headers :
    request.headers.append('X-Workspace-ID', workspace ? (JSON.parse(workspace) as IWorkspace).id : '');

  const props = {
    headers,
    withCredentials: true,
  };

  const baseRequest = request.clone({
    headers,
  });

  if (isProduction) {
    return next(baseRequest);
  }

  const authToken = localStorage.getItem(SESSION_TOKEN_KEY);

  if (environment.local) {
    headers = headers.append('Authorization', 'Bearer' + ' ' + authToken);
    props.headers = headers;
    props.withCredentials = false;
  }

  // Clone the request to add the authentication header.
  const newReq = baseRequest.clone(props);

  return next(newReq);
};

export const sessionExpirationInterceptor: HttpInterceptorFn = (request, next) => {
  return next(request.clone()).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === HttpStatusCode.Unauthorized) {
        console.error(`Session expired: ${error.message}`)

        goToSafePlaceLogin();
      }

      if (error.status === HttpStatusCode.Forbidden) {
        console.error(`Session expired: ${error.message}`)

        goToSafePlaceLogin();
      }

      return throwError(() => error);
    }),
    map((event: HttpEvent<unknown>) => event)
  );
};

@NgModule({
  declarations: [
    AppComponent,
    ConsoleWrapperComponent,
    ConsoleComponent,
    AshgridxLogoComponent,
    AccountSettingsComponent,
    TeamMembersComponent,
    AddTeamMembersComponent,
    SwitchWorkspaceComponent,
    BillingComponent,
    BillingActivityComponent,
    BillingMonthlyCostComponent,
    BillingPaymentMethodComponent,
    AddPaymentMethodComponent,
    CreateNewSiteComponent,
    SiteComponent,
    ViewSiteComponent,
    CompanyDetailsComponent,
    ProfileDetailsComponent,
    ManagePrivilegeComponent,
    AddPrivilegeComponent,
    PrivilegeComponent,
    NoWorkspaceComponent,
    EditUserProfileComponent,
    NoPrivilegeComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    MatSidenavModule,
    MatExpansionModule,
    AuditModule,
    SharedModule,
    ...SharedModule.sharedRootModules(),
    NgxEchartsModule.forRoot({
      /**
       * This will import all modules from echarts.
       * If you only need custom modules,
       * please refer to [Custom Build] section.
       */
      echarts,
    }),
    GoogleMap,
    MapMarker,
    MapInfoWindow,
    MonacoEditorModule.forRoot(),
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatSidenav,
    MatSidenavContainer,
    AshGridXModule,
    SurechargeModule,
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireAuthModule,
    AngularFireStorageModule,
  ],
  providers: [
    provideHttpClient(
      withFetch(),
      withInterceptors([progressBarInterceptor, authTokenInterceptor, sessionExpirationInterceptor])
    ),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideStorage(() => getStorage()),
    ...SharedModule.sharedProviders(),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
